import { useMemo } from 'react';

import { Button } from '#/components/Button/Button';
import { CheckboxPills } from '#/components/CheckboxPills/CheckboxPills';
import { Divider } from '#/components/Divider/Divider';
import { ScrollArea } from '#/components/ScrollArea/ScrollArea';
import { Stack } from '#/components/Stack/Stack';
import { BodySmall } from '#/components/Text/BodySmall';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiCategory } from '#/modules/cms/api/types/CmsApiCategory';
import type { CmsApiCategorySlug } from '#/modules/cms/api/types/CmsApiTypedId';
import { spacing } from '#/theme/theme';

import {
  PostSearchSideFacetFilterHolder,
  PostSearchSideFacetFilterRelatedHolder,
} from './PostSearchCategories.styled';
import { PostSearchCategory } from './PostSearchCategory';

type Props = {
  label: string | null;
  allCategories: CmsApiCategory[] | undefined;
  selectedCategorySlugs: string[];
  onSelectParentCategory: (categorySlug: CmsApiCategorySlug) => void;
  onDeselectParentCategory: () => void;
  toggleSubCategory: (categorySlug: CmsApiCategorySlug[]) => void;
};

export function PostSearchCategoryFilter({
  label,
  allCategories,
  selectedCategorySlugs,
  onSelectParentCategory,
  onDeselectParentCategory,
  toggleSubCategory,
}: Props) {
  const { i18n } = useI18n();

  const parentCategories =
    allCategories?.filter((category) => !category.parentId) ?? [];

  const selectedParentCategory = useMemo(() => {
    if (!allCategories) return;

    const selectedCategories = allCategories.filter((category) =>
      selectedCategorySlugs.includes(category.slug),
    );

    if (selectedCategories.length === 0) return;

    const firstItemParentId = selectedCategories[0]?.parentId;
    if (!firstItemParentId) return selectedCategories[0];

    return allCategories.find((category) => category.id === firstItemParentId);
  }, [allCategories, selectedCategorySlugs]);

  const selectedSubCategorySlugs = useMemo(() => {
    if (!allCategories) return [];

    const selectedSubCategories = allCategories.filter(
      (category) =>
        category.parentId && selectedCategorySlugs.includes(category.slug),
    );

    return selectedSubCategories.map((subCategory) => subCategory.slug);
  }, [allCategories, selectedCategorySlugs]);

  const subcategories =
    allCategories?.filter(
      (category) => category.parentId === selectedParentCategory?.id,
    ) ?? [];

  return (
    <div data-qa-id="side-filter-facet-categories">
      <PostSearchSideFacetFilterHolder>
        <BodySmall>{label ?? i18n('Category')}</BodySmall>

        {selectedCategorySlugs.length > 0 && (
          <Button
            type="button"
            variant="tertiary"
            onClick={onDeselectParentCategory}
          >
            <Text styleAs="h5">
              {
                /* TRANSLATORS: clear all filters in article search */
                i18n('Clear')
              }
            </Text>
          </Button>
        )}
      </PostSearchSideFacetFilterHolder>

      <ScrollArea
        direction="horizontal"
        qaId="search-filter-section-filters"
        hideScrollBar
        width="100%"
      >
        <Stack direction={{ xs: 'row', md: 'column' }} spacing={spacing[4]}>
          {parentCategories.map((parentCategory) => {
            const selected = parentCategory.id === selectedParentCategory?.id;

            return (
              <PostSearchCategory
                key={parentCategory.slug}
                selected={selected}
                title={parentCategory.name}
                icon={parentCategory.icon}
                onClick={() =>
                  selected
                    ? onDeselectParentCategory()
                    : onSelectParentCategory(parentCategory.slug)
                }
                qaId="side-filter"
                data-qa-selected={selected}
                data-qa-value={parentCategory.slug}
              />
            );
          })}
        </Stack>
      </ScrollArea>

      {subcategories.length > 0 && (
        <>
          <Divider marginTop={36} marginBottom={36} />

          <PostSearchSideFacetFilterHolder>
            <BodySmall>{i18n('Related')}</BodySmall>
          </PostSearchSideFacetFilterHolder>

          <PostSearchSideFacetFilterRelatedHolder>
            <CheckboxPills
              size="small"
              options={subcategories.map((sc) => ({
                label: sc.name,
                value: sc.slug,
              }))}
              name="categories"
              values={selectedSubCategorySlugs}
              onChange={(value) => toggleSubCategory(value)}
            />
          </PostSearchSideFacetFilterRelatedHolder>
        </>
      )}
    </div>
  );
}
